import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PropTypes from "prop-types";
import { Box, Button, Checkbox, Grid } from "@mui/material";

import InputFieldText from "Components/Common/InputFieldText";

import {
  fireDetectionTypeEnum,
  fireOperationTypeEnum,
  getOptionValue,
} from "common/enums";

import SelectField from "Components/Common/SelectField";

const FireDetectionSystem = ({ formik, t, floorOptions }) => {
  const {
    setFieldValue,
    setFieldTouched,
    values: {
      fireProtection: { detectorSystems },
    },
  } = formik;

  const handleAddDetectionSystem = (existingDetectorSystem) => {
    const newDetectorSystem = [
      ...existingDetectorSystem,
      {
        id: 0,
        floorId: "",
        operationType: "",
        makeForOther: "",
        makeForGasLeak: "",
        detectionTypes: [],
        numberOfDetectionType: "",
      },
    ];
    setFieldValue("fireProtection.detectorSystems", newDetectorSystem);
    setFieldTouched(
      `fireProtection.detectorSystems[${newDetectorSystem.length - 1}]`,
      false,
      false
    );
  };

  const handleRemoveDetectorSystem = (index) => {
    setFieldValue(
      "fireProtection.detectorSystems",
      detectorSystems.filter((_, ind) => ind !== index)
    );
  };

  useEffect(() => {
    if (!detectorSystems.length) {
      setFieldValue("fireProtection.detectorSystems", [
        {
          id: 0,
          floorId: "",
          operationType: "",
          makeForOther: "",
          makeForGasLeak: "",
          detectionTypes: [],
          numberOfDetectionType: "",
        },
      ]);
    }
  }, [detectorSystems]);

  return (
    <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1, pt: 1 }}>
      <Grid item className="border ps-4 pe-2 pb-2 pt-2" sx={{ mb: 3 }} xs={12}>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={2}
          sx={{ pl: 1, minHeight: "70px" }}
        >
          <Box sx={{ mt: 1 }}>
            <Button
              color="primary"
              sx={{ minWidth: "unset", textTransform: "capitalize" }}
              variant="outlined"
              onClick={() => handleAddDetectionSystem(detectorSystems)}
            >
              <strong>
                <span>+</span> {t("Add ( AFDAS / MOFAS )")}
              </strong>
            </Button>
          </Box>

          {!!detectorSystems.length &&
            detectorSystems.map(
              ({ detectionTypes, numberOfDetectionType }, index) => {
                const isGasLeakDetectionType = (detectionTypes) =>
                  detectionTypes.some(
                    ({ value }) =>
                      value ===
                      getOptionValue(
                        "fireDetectionTypeEnum",
                        "Gas Leak Detection System"
                      )
                  );
                const isOtherDetectionType = (detectionTypes) =>
                  detectionTypes.some(
                    ({ value }) =>
                      value !==
                      getOptionValue(
                        "fireDetectionTypeEnum",
                        "Gas Leak Detection System"
                      )
                  );

                return (
                  <Grid
                    key={index}
                    container
                    columnSpacing={1}
                    rowSpacing={1}
                    className="border p-2 pe-3 mt-2"
                  >
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <SelectField
                        label={t("Floor") + "*"}
                        name={`fireProtection.detectorSystems.${index}.floorId`}
                        formik={formik}
                        options={floorOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <SelectField
                        label={t("Operation Type") + "*"}
                        name={`fireProtection.detectorSystems.${index}.operationType`}
                        formik={formik}
                        options={fireOperationTypeEnum}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <SelectField
                        label={t("Detection Types")}
                        name={`fireProtection.detectorSystems.${index}.detectionTypes`}
                        formik={formik}
                        options={fireDetectionTypeEnum}
                        multiple
                        disableCloseOnSelect
                        isOptionEqualToValue={(option, { value }) =>
                          option.value === value
                        }
                        renderOption={(props, option, { selected }) => {
                          return (
                            <li {...props} key={option.value}>
                              <Checkbox
                                sx={{ marginRight: "8px" }}
                                checked={selected}
                              />
                              {option.label}
                            </li>
                          );
                        }}
                        onChange={(event, options) => {
                          if (
                            event.key !== "Backspace" &&
                            event.key !== "Delete"
                          ) {
                            setFieldValue(
                              `fireProtection.detectorSystems.${index}.detectionTypes`,
                              options
                            );
                            setFieldValue(
                              `fireProtection.detectorSystems.${index}.numberOfDetectionType`,
                              options.length
                            );

                            if (!isGasLeakDetectionType(options)) {
                              setFieldValue(
                                `fireProtection.detectorSystems.${index}.makeForGasLeak`,
                                ""
                              );
                            }
                            if (!isOtherDetectionType(options)) {
                              setFieldValue(
                                `fireProtection.detectorSystems.${index}.makeForOther`,
                                ""
                              );
                            }
                          }
                        }}
                        value={detectionTypes || []}
                      />
                    </Grid>

                    {isOtherDetectionType(detectionTypes) && (
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <InputFieldText
                          label={t("Make") + "*"}
                          name={`fireProtection.detectorSystems.${index}.makeForOther`}
                          formik={formik}
                          placeholder="Enter Make Details"
                        />
                      </Grid>
                    )}

                    {isGasLeakDetectionType(detectionTypes) && (
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <InputFieldText
                          label={t("Make For Gas Leak") + "*"}
                          name={`fireProtection.detectorSystems.${index}.makeForGasLeak`}
                          formik={formik}
                          placeholder="Enter Make For Gas Leak Details"
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <InputFieldText
                        label={t("Number of Detection Type") + "*"}
                        name={`fireProtection.detectorSystems.${index}.numberOfDetectionType`}
                        formik={formik}
                        value={numberOfDetectionType ?? 0}
                        placeholder="Enter Type of Detection System"
                        disabled
                      />
                    </Grid>

                    {detectorSystems.length > 1 && (
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Button
                          color="error"
                          size="small"
                          sx={{
                            minWidth: "unset",
                            border: "1px solid",
                            height: "40px",
                            width: "100px",
                            mb: 2,
                          }}
                          onClick={() => handleRemoveDetectorSystem(index)}
                        >
                          <strong className="d-flex justify-content-center">
                            <DeleteOutlineSharpIcon fontSize="small" />
                            {t("Remove")}
                          </strong>
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                );
              }
            )}
        </Grid>
      </Grid>
    </Grid>
  );
};

FireDetectionSystem.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
  floorOptions: PropTypes.array,
};

export default withTranslation()(FireDetectionSystem);
